import { Controller } from "@hotwired/stimulus";

// Drives the settings-style sidebar for the JuniorConfiguration edit page.
// All section panels live in the DOM at once (the page is admin, volumes are
// small) — we just toggle [hidden] and the active link. Section state is
// reflected in the URL hash so reloads and shared links land on the right
// panel; a localStorage fallback remembers the last visited section per
// configuration when the URL has no hash.
export default class extends Controller {
  static targets = ["panel", "link"];
  static values = {
    defaultSection: { type: String, default: "profile" },
    storageKey:     { type: String, default: "" } // "jc:lastSection:<id>"
  };

  connect() {
    this._onHashChange = this._onHashChange.bind(this);
    window.addEventListener("hashchange", this._onHashChange);
    this._activate(this._initialSection(), { updateHash: false });
  }

  disconnect() {
    window.removeEventListener("hashchange", this._onHashChange);
  }

  // Triggered by clicks on sidebar links: <a data-action="click->junior-config-nav#select" data-section="poles">
  select(event) {
    event.preventDefault();
    const slug = event.currentTarget.dataset.section;
    if (!slug) return;
    this._activate(slug, { updateHash: true });
  }

  _onHashChange() {
    const slug = (window.location.hash || "").replace(/^#/, "");
    if (!slug) return;
    if (!this._panelFor(slug)) return;
    this._activate(slug, { updateHash: false });
  }

  _initialSection() {
    const fromHash = (window.location.hash || "").replace(/^#/, "");
    if (fromHash && this._panelFor(fromHash)) return fromHash;

    if (this.storageKeyValue) {
      try {
        const stored = window.localStorage.getItem(this.storageKeyValue);
        if (stored && this._panelFor(stored)) return stored;
      } catch (_e) { /* localStorage unavailable; ignore */ }
    }
    return this.defaultSectionValue;
  }

  _activate(slug, { updateHash }) {
    const panel = this._panelFor(slug);
    if (!panel) return;

    this.panelTargets.forEach((p) => {
      const isActive = p === panel;
      p.toggleAttribute("hidden", !isActive);
      p.classList.toggle("jc-section--active", isActive);
    });

    this.linkTargets.forEach((link) => {
      link.classList.toggle("jc-sidebar__link--active", link.dataset.section === slug);
    });

    if (updateHash) {
      // replaceState avoids polluting browser history with every section click.
      window.history.replaceState(null, "", `#${slug}`);
    }

    if (this.storageKeyValue) {
      try { window.localStorage.setItem(this.storageKeyValue, slug); } catch (_e) { /* ignore */ }
    }

    // Scroll the content area to its top so a section opened by hash doesn't
    // inherit the previous panel's scroll offset.
    const scroller = this.element.querySelector("[data-junior-config-nav-scroller]");
    if (scroller) scroller.scrollTop = 0;
  }

  _panelFor(slug) {
    return this.panelTargets.find((p) => p.dataset.section === slug);
  }
}
